import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, CustomInput, FormGroup, Input, FormFeedback } from 'reactstrap';
import { LProps, withLocalization } from '../utils/wrappers';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { State, Modals } from '../redux/state';
import actions from '../redux/actions';
import { connect } from 'react-redux';
import { CalculatorSnapshot, formatComparisonText, normalizeCalculatorSnapshot, hasCategoryBreakdown } from '../utils/calculatorSnapshot';
import { CALCULATOR_REQUIREMENTS_MESSAGE } from '../utils/calculatorProgress';
import { STORY_MIN_CHARS } from '../utils/storyMinChars';

const CATEGORY_ROWS: Array<{ key: 'transport' | 'housing' | 'food' | 'goods' | 'services'; label: string }> = [
  { key: 'transport', label: 'Transportation' },
  { key: 'housing', label: 'Housing' },
  { key: 'food', label: 'Food' },
  { key: 'goods', label: 'Goods' },
  { key: 'services', label: 'Services' }
];

interface Props {
  isOpen: boolean;
  shareOnFeed: boolean;
  submitting: boolean;
  snapshot?: CalculatorSnapshot;
  progressReadyForPoints: boolean;
  loadCalculatorChartPreview: () => Promise<string>;
  completeCalculatorWithShareChoice: (shareOnFeed: boolean, reflectionText: string) => void;
  setCalculatorShareOnFeed: (shareOnFeed: boolean) => void;
  returnToCalculatorFromShareModal: () => void;
}

interface CalculatorShareModalState {
  reflectionText: string;
  submitted: boolean;
  chartPreviewUrl: string;
  chartLoading: boolean;
}

const REFLECTION_PROMPT = 'What did you learn about your carbon footprint?';

class CalculatorShareModal extends React.Component<LProps<Props>, CalculatorShareModalState> {
  private chartLoadSeq = 0;

  public state: CalculatorShareModalState = {
    reflectionText: '',
    submitted: false,
    chartPreviewUrl: '',
    chartLoading: false
  };

  public componentWillReceiveProps(nextProps: LProps<Props>) {
    if (nextProps.isOpen && !this.props.isOpen) {
      this.setState({ reflectionText: '', submitted: false, chartPreviewUrl: '', chartLoading: true });
      this.loadChartPreview();
      return;
    }
    // loadCalculatorChartPreview patches Redux with an enriched snapshot, which
    // re-enters here. Do not restart while a load is in flight or we already have
    // a chart — that left the modal stuck on "Loading chart...".
    if (!nextProps.isOpen || this.state.chartLoading || this.state.chartPreviewUrl) {
      return;
    }
    if (nextProps.snapshot === this.props.snapshot) {
      return;
    }
    const nextCategories = nextProps.snapshot && nextProps.snapshot.categories;
    if (hasCategoryBreakdown(nextCategories || {
      transport: 0, housing: 0, food: 0, goods: 0, services: 0
    })) {
      this.loadChartPreview();
    }
  }

  private loadChartPreview = async () => {
    const { loadCalculatorChartPreview } = this.props;
    const seq = ++this.chartLoadSeq;
    this.setState({ chartLoading: true });
    try {
      const chartPreviewUrl = await loadCalculatorChartPreview();
      if (seq !== this.chartLoadSeq) {
        return;
      }
      this.setState({ chartPreviewUrl: chartPreviewUrl || '', chartLoading: false });
    } catch (_err) {
      if (seq !== this.chartLoadSeq) {
        return;
      }
      this.setState({ chartLoading: false });
    }
  }

  private finish() {
    const { shareOnFeed, completeCalculatorWithShareChoice, submitting } = this.props;
    const { reflectionText } = this.state;
    if (submitting) {
      return;
    }
    if (reflectionText.trim().length < STORY_MIN_CHARS) {
      this.setState({ submitted: true });
      return;
    }
    completeCalculatorWithShareChoice(shareOnFeed, reflectionText);
  }

  public render() {
    const {
      t, isOpen, shareOnFeed, snapshot, setCalculatorShareOnFeed, submitting,
      progressReadyForPoints, returnToCalculatorFromShareModal
    } = this.props;
    const { reflectionText, submitted, chartPreviewUrl, chartLoading } = this.state;
    const normalizedSnapshot = snapshot ? normalizeCalculatorSnapshot(snapshot) : undefined;
    const reflectionPrompt = t(REFLECTION_PROMPT);
    const reflectionTooShort = reflectionText.trim().length < STORY_MIN_CHARS;
    const canSubmit = progressReadyForPoints;
    if (!isOpen || !normalizedSnapshot || normalizedSnapshot.footprintMt <= 0) {
      return null;
    }
    return (
      <Modal isOpen={isOpen} id="cc__calculator_share_modal" backdrop="static">
        <ModalHeader>
          {t('Submit your calculator action')}
        </ModalHeader>
        <ModalBody>
          {!canSubmit &&
            <p className="text-warning">{t(CALCULATOR_REQUIREMENTS_MESSAGE)}</p>
          }
          <p>{formatComparisonText(normalizedSnapshot)}</p>
          {chartPreviewUrl &&
            <div className="cc__calculator_share_modal__chart">
              <img src={chartPreviewUrl} alt={t('Carbon footprint chart')} />
            </div>
          }
          {!chartPreviewUrl && chartLoading &&
            <p className="cc__lighttext">{t('Loading chart...')}</p>
          }
          {hasCategoryBreakdown(normalizedSnapshot.categories) && !chartPreviewUrl &&
            <div className="cc__calculator_share_modal__totals" style={{ marginBottom: '16px' }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{t('Category')}</th>
                    <th className="text-right">{t('mt CO₂e')}</th>
                  </tr>
                </thead>
                <tbody>
                  {CATEGORY_ROWS.map((row) =>
                    normalizedSnapshot.categories[row.key] > 0 ? (
                      <tr key={row.key}>
                        <td>{t(row.label)}</td>
                        <td className="text-right">
                          {normalizedSnapshot.categories[row.key].toFixed(1)}
                        </td>
                      </tr>
                    ) : null
                  )}
                  <tr>
                    <td><strong>{t('Total')}</strong></td>
                    <td className="text-right">
                      <strong>{normalizedSnapshot.footprintMt.toFixed(1)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          }
          <p className="cc__lighttext" style={{ marginBottom: '8px' }}>
            {t('Write a short reflection to earn your points. Sharing your footprint chart is optional.')}
          </p>
          <div>
            <FormGroup className="cc__story_form__input cc__lighttext">
              <Input
                aria-label={reflectionPrompt}
                type="textarea"
                id="cc__calculator_share_reflection"
                placeholder={reflectionPrompt}
                value={reflectionText}
                onChange={(ev) => this.setState({ reflectionText: ev.target.value, submitted: false })}
              />
            </FormGroup>
            {(submitted && reflectionTooShort) &&
              <FormFeedback style={{ display: 'block' }}>
                {t('Stories require {{min}} minimum characters. You\'ve entered', { min: STORY_MIN_CHARS })}&nbsp;
                <span style={{ width: '16px', display: 'inline-block', textAlign: 'right' }}>
                  {reflectionText.trim().length}
                </span>&nbsp;
                {reflectionText.trim().length === 1 ? t('character') : t('characters')}.
              </FormFeedback>
            }
          </div>
          <CustomInput
            id="cc__calculator_share_checkbox"
            type="checkbox"
            checked={shareOnFeed}
            onChange={(ev) => {
              const checked = ev.target.checked;
              setCalculatorShareOnFeed(checked);
              this.setState({ submitted: false });
            }}
            label={t('Also share my carbon footprint chart on my profile and newsfeeds')}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline={true}
            disabled={submitting}
            onClick={() => returnToCalculatorFromShareModal()}
          >{t('Review my Summary')}</Button>
          <Button
            color="primary"
            disabled={submitting || !canSubmit}
            onClick={() => this.finish()}
          >{t('Submit')}</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State, ownProps: {
  progressReadyForPoints?: boolean;
}): Partial<Props> => {
  const snapshot = state.calculatorSnapshot
    ? normalizeCalculatorSnapshot(state.calculatorSnapshot)
    : undefined;
  const isOpen = state.modal === Modals.CalculatorShare
    && Boolean(snapshot)
    && snapshot.footprintMt > 0;
  return {
    isOpen,
    shareOnFeed: state.calculatorShareOnFeed === true,
    submitting: Boolean(state.spinner),
    snapshot,
    progressReadyForPoints: ownProps.progressReadyForPoints !== false
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  loadCalculatorChartPreview: actions.loadCalculatorChartPreview,
  completeCalculatorWithShareChoice: actions.completeCalculatorWithShareChoice,
  setCalculatorShareOnFeed: actions.setCalculatorShareOnFeed,
  returnToCalculatorFromShareModal: actions.returnToCalculatorFromShareModal
}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('calculator')
)(CalculatorShareModal) as React.ComponentClass;
